import {
  AlertTriangle,
  Check,
  Flag,
  Link2,
  Lock,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { IconButton } from "@/components/cinch/IconButton";
import { KpiTile } from "@/components/cinch/KpiTile";
import { BulkActionBar } from "@/components/cinch/BulkActionBar";
import { CollapsibleCard } from "@/components/cinch/CollapsibleCard";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import {
  IntegrationBulkUploadDialog,
  type BulkRow,
} from "./IntegrationBulkUploadDialog";
import {
  useConfirmIntegrationMapping,
  useCreateIntegrationInvoice,
  useDeleteIntegrationInvoice,
  useImportIntegrationLines,
  useIntegrationCandidates,
  useIntegrationExpense,
  useBulkReviewIntegrationLines,
  useMatchIntegrationExpense,
  useReviewIntegrationLine,
  VERDICT_META,
  type IntegrationInvoice,
  type IntegrationLine,
  type IntegrationNotInvoiced,
  type IntegrationQbCost,
  type IntegrationVendor,
} from "../api";

const money = (n: number | null | undefined) =>
  n == null
    ? "—"
    : n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const MODE_HINT: Record<string, string> = {
  line: "One invoice line per location — fully reconcilable.",
  count: "The invoice states a quantity and names nobody — reconciled as a count delta.",
  listed:
    "Not reconciled — by design. Recorded so the month's expense total is complete.",
};

/** Verdicts and vendor modes are statuses, so they go through StatusBadge —
 *  its map owns the tone for every one (see StatusBadge.tsx). The wrapping span
 *  only carries the hover explanation; the chip itself is pure DS. */
function HintedStatus({ value, hint }: { value: string | null; hint?: string }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span title={hint}>
      <StatusBadge value={value} />
    </span>
  );
}

export function IntegrationExpensePanel({ period }: { period: string }) {
  const { data, isLoading } = useIntegrationExpense(period);
  const match = useMatchIntegrationExpense(period);
  const review = useReviewIntegrationLine(period);
  const createInvoice = useCreateIntegrationInvoice(period);
  const deleteInvoice = useDeleteIntegrationInvoice(period);
  const importLines = useImportIntegrationLines(period);
  const bulkReview = useBulkReviewIntegrationLines(period);

  const [tab, setTab] = useState("invoices");
  // Multi-select, per the FilterBar idiom: "churned AND no data" is a question
  // worth asking, and a single-select picker couldn't express it.
  const [verdictFilter, setVerdictFilter] = useState<string[]>([]);
  const [vendorFilter, setVendorFilter] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const canAct = data?.can_act ?? false;
  const locked = data?.locked ?? false;
  const summary = data?.summary;

  const lines = useMemo(() => {
    let rows = data?.lines ?? [];
    if (vendorFilter.length)
      rows = rows.filter((r) => r.vendor_slug && vendorFilter.includes(r.vendor_slug));
    if (verdictFilter.length)
      rows = rows.filter((r) => r.verdict && verdictFilter.includes(r.verdict));
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.vendor_location_ref, r.vendor_location_name, r.location_name, r.company_name, r.vendor_name]
          .some((v) => (v ?? "").toLowerCase().includes(q)),
      );
    }
    return rows;
  }, [data?.lines, vendorFilter, verdictFilter, search]);

  /** Vendors that actually have reconciled lines this period, with counts —
   *  the catalog has ten, but only the ones with an invoice are worth listing.
   *  Counts respect the verdict filter so the two read together. */
  const vendorCounts = useMemo(() => {
    const base = verdictFilter.length
      ? (data?.lines ?? []).filter((r) => r.verdict && verdictFilter.includes(r.verdict))
      : (data?.lines ?? []);
    const counts = new Map<string, { name: string; n: number }>();
    for (const row of base) {
      const slug = row.vendor_slug;
      if (!slug) continue;
      const prev = counts.get(slug);
      counts.set(slug, { name: row.vendor_name ?? slug, n: (prev?.n ?? 0) + 1 });
    }
    return [...counts.entries()].sort((a, b) => b[1].n - a[1].n);
  }, [data?.lines, verdictFilter]);

  /** What's on, as removable pills. Vendor labels come from the roster rather
   *  than the slug so a pill reads "ISI Software", not "isi". */
  const activeFilters = useMemo(() => {
    const names = new Map(vendorCounts.map(([slug, v]) => [slug, v.name]));
    return [
      ...vendorFilter.map((v) => ({
        kind: "vendor" as const,
        value: v,
        label: names.get(v) ?? v,
      })),
      ...verdictFilter.map((v) => ({
        kind: "verdict" as const,
        value: v,
        label: VERDICT_META[v]?.label ?? v,
      })),
    ];
  }, [vendorFilter, verdictFilter, vendorCounts]);

  async function quickReview(row: IntegrationLine, status: "approved" | "flagged") {
    try {
      await review.mutateAsync({ item_key: row.item_key, review_status: status });
      toast.success(status === "approved" ? "Approved" : "Flagged");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save that review");
    }
  }

  /** Applies to exactly the rows the table is showing — `lines` is already the
   *  filtered+searched set, so scope and screen agree by construction. */
  async function bulkReviewVisible(status: "approved" | "flagged") {
    try {
      const res = await bulkReview.mutateAsync({
        item_keys: lines.map((r) => r.item_key),
        review_status: status,
      });
      const verb = status === "approved" ? "Approved" : "Flagged";
      toast.success(
        res.updated === res.requested
          ? `${verb} ${res.updated} line${res.updated === 1 ? "" : "s"}`
          : `${verb} ${res.updated} of ${res.requested} — the rest are no longer in this worklist`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk review failed");
    }
  }

  async function runMatch() {
    try {
      const res = await match.mutateAsync();
      toast.success(
        `Matched ${res.matched} line${res.matched === 1 ? "" : "s"}` +
          (res.unmapped ? ` · ${res.unmapped} still unmapped` : "") +
          (res.not_invoiced ? ` · ${res.not_invoiced} live but not billed` : ""),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Match failed");
    }
  }

  if (isLoading && !data) return <Card className="h-64 animate-pulse" />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiTile label="Invoiced" value={money(summary?.invoiced_total ?? 0)} />
        <KpiTile
          label="Exceptions"
          value={money(summary?.exception_amount ?? 0)}
          sub={`${summary?.exception_lines ?? 0} lines`}
          tone={(summary?.exception_lines ?? 0) > 0 ? "warning" : "neutral"}
        />
        <KpiTile label="Unmapped" value={String(summary?.unmapped ?? 0)} />
        <KpiTile
          label="Flagged"
          value={String(summary?.flagged ?? 0)}
          tone={(summary?.flagged ?? 0) > 0 ? "warning" : "neutral"}
        />
        <KpiTile
          label="Listed (not reconciled)"
          value={money(summary?.listed_total ?? 0)}
          sub="by design"
          info="MOTOR and AutoNetTV are recorded so the month's expense total is complete, but are never matched to locations."
        />
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={runMatch} disabled={!canAct || match.isPending}>
          <RefreshCw className={cn("mr-2 h-4 w-4", match.isPending && "animate-spin")} />
          {data?.matched_at ? "Re-run match" : "Run match"}
        </Button>
        {data?.matched_at && (
          <span className="text-xs text-muted-foreground">
            Matched {new Date(data.matched_at).toLocaleString()}
            {data.matched_by_name ? ` by ${data.matched_by_name}` : ""}
          </span>
        )}
        {locked && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" /> Step complete — locked
          </span>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="invoices">
            Invoices ({data?.invoices.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="reconciliation">
            Reconciliation ({data?.lines.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="mapping">
            Mapping ({data?.unmapped.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="howto">How to</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <InvoicesTab
            period={period}
            vendors={data?.vendors ?? []}
            invoices={data?.invoices ?? []}
            qbCosts={data?.qb_costs ?? []}
            canAct={canAct}
            onCreate={createInvoice}
            onDelete={deleteInvoice}
            onImport={importLines}
          />
        </TabsContent>

        <TabsContent value="reconciliation" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Reconciled lines</CardTitle>
            </CardHeader>
            <CardContent>
              <FilterBar>
                <FilterSearch
                  value={search}
                  onChange={setSearch}
                  placeholder="Search ref, location, company…"
                />
                <FilterChip label="Vendor" count={vendorFilter.length}>
                  <CheckboxList
                    searchable={vendorCounts.length > 6}
                    options={vendorCounts.map(([slug, { name, n }]) => ({
                      value: slug,
                      label: name,
                      count: n,
                    }))}
                    selected={vendorFilter}
                    onChange={setVendorFilter}
                    emptyText="No vendors reconciled yet"
                  />
                </FilterChip>
                <FilterChip label="Verdict" count={verdictFilter.length}>
                  <CheckboxList
                    options={Object.entries(summary?.by_verdict ?? {})
                      .sort(([, a], [, b]) => b - a)
                      .map(([v, n]) => ({
                        value: v,
                        label: VERDICT_META[v]?.label ?? v,
                        count: n,
                      }))}
                    selected={verdictFilter}
                    onChange={setVerdictFilter}
                    emptyText="Run the match first"
                  />
                </FilterChip>
                {activeFilters.map((f) => (
                  <ActiveFilterPill
                    key={`${f.kind}:${f.value}`}
                    label={f.label}
                    onRemove={() =>
                      f.kind === "vendor"
                        ? setVendorFilter(vendorFilter.filter((v) => v !== f.value))
                        : setVerdictFilter(verdictFilter.filter((v) => v !== f.value))
                    }
                  />
                ))}
                {activeFilters.length > 0 && (
                  <FilterClearAll
                    onClick={() => {
                      setVendorFilter([]);
                      setVerdictFilter([]);
                    }}
                  />
                )}
              </FilterBar>
              {canAct && (
                <BulkActionBar
                  className="mt-3"
                  count={lines.length}
                  totalCount={data?.lines.length ?? 0}
                  noun="line"
                  actions={[
                    {
                      label: "Approve",
                      tone: "success",
                      icon: <Check className="h-3.5 w-3.5" />,
                      disabled: bulkReview.isPending,
                      onClick: () => bulkReviewVisible("approved"),
                    },
                    {
                      label: "Flag",
                      tone: "warning",
                      icon: <Flag className="h-3.5 w-3.5" />,
                      disabled: bulkReview.isPending,
                      onClick: () => bulkReviewVisible("flagged"),
                    },
                  ]}
                />
              )}

              <div className="mt-4">
              {lines.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {data?.matched_at
                    ? "No lines match those filters."
                    : "Record the month's invoices, then run the match."}
                </p>
              ) : (
                <DataTable
                  rows={lines}
                  rowKey={(r) => r.item_key}
                  columns={lineColumns(canAct, quickReview)}
                />
              )}
              </div>
            </CardContent>
          </Card>

          <NotInvoicedPanel groups={data?.not_invoiced ?? []} />
        </TabsContent>

        <TabsContent value="mapping" className="mt-4">
          <MappingTab
            period={period}
            unmapped={data?.unmapped ?? []}
            vendors={data?.vendors ?? []}
            canAct={canAct}
          />
        </TabsContent>

        <TabsContent value="howto" className="mt-4">
          <HowToTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function lineColumns(
  canAct: boolean,
  quickReview: (row: IntegrationLine, status: "approved" | "flagged") => void,
): DataTableColumn<IntegrationLine>[] {
  return [
    { key: "vendor_name", header: "Vendor", render: (r) => r.vendor_name ?? "—" },
    {
      key: "vendor_location_ref",
      header: "Vendor ref",
      render: (r) => (
        <span className="font-mono text-xs">{r.vendor_location_ref ?? "—"}</span>
      ),
    },
    {
      key: "vendor_location_name",
      header: "Billed as",
      render: (r) => r.vendor_location_name ?? "—",
    },
    {
      key: "location_name",
      header: "Cinch location",
      render: (r) => {
        // Count mode names nobody on the invoice, but Cinch knows exactly which
        // locations are on the feed. Showing them by company turns "we can't
        // tell" into a checkable lead — a company whose rooftop count equals the
        // invoiced quantity is almost certainly what the invoice covers.
        if (r.mode === "count") {
          const roster = r.roster_by_company ?? [];
          if (roster.length === 0)
            return <span className="text-warning">nothing on this feed</span>;
          return (
            <span className="flex flex-col gap-0.5">
              {roster.slice(0, 4).map((c) => (
                <span
                  key={c.company_name}
                  className={cn(
                    "text-xs",
                    c.matches_invoiced_quantity
                      ? "font-medium text-success"
                      : "text-muted-foreground",
                  )}
                >
                  {c.company_name} · {c.locations}
                  {c.matches_invoiced_quantity && " ← matches billed qty"}
                </span>
              ))}
              {roster.length > 4 && (
                <span className="text-xs text-muted-foreground">
                  +{roster.length - 4} more
                </span>
              )}
            </span>
          );
        }
        return r.location_name ? (
          <span>
            {r.location_name}
            {r.company_name && (
              <span className="block text-xs text-muted-foreground">{r.company_name}</span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground">not mapped</span>
        );
      },
    },
    {
      key: "txns_in_period",
      header: "Txns",
      align: "right",
      sortable: true,
      render: (r) =>
        r.mode === "count" ? (
          <span className="text-xs">
            {r.invoiced_quantity} billed / {r.evidenced} evidenced
          </span>
        ) : r.mode === "listed" ? (
          // Usage, not a reconciliation — a flat licence nobody uses is a
          // value question, so it's shown but never coloured as a problem.
          <span className="text-xs text-muted-foreground" title={r.usage_note}>
            {r.evidenced} of {r.known_locations} in use
          </span>
        ) : (
          <span className={cn(r.txns_in_period === 0 && "text-warning")}>
            {r.txns_in_period.toLocaleString()}
          </span>
        ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      sortable: true,
      render: (r) => money(r.amount),
    },
    {
      key: "verdict",
      header: "Verdict",
      sortable: true,
      render: (r) => <HintedStatus value={r.verdict} hint={VERDICT_META[r.verdict ?? ""]?.hint} />,
    },
    {
      key: "review",
      header: "Review",
      align: "right",
      className: "w-40",
      render: (r) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          <StatusBadge value={r.review_status} />
          {canAct && (
            <>
              <IconButton
                title={`Approve ${r.vendor_location_ref ?? r.item_key} — this charge is correct`}
                active={r.review_status === "approved"}
                activeClassName="text-success"
                hoverClassName="hover:bg-success-light hover:text-success"
                onClick={() => quickReview(r, "approved")}
              >
                <Check className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                title={`Flag ${r.vendor_location_ref ?? r.item_key} — raise with the vendor (blocks completing R-18)`}
                active={r.review_status === "flagged"}
                activeClassName="text-warning"
                hoverClassName="hover:bg-warning-light hover:text-warning"
                onClick={() => quickReview(r, "flagged")}
              >
                <Flag className="h-3.5 w-3.5" />
              </IconButton>
            </>
          )}
        </span>
      ),
    },
  ];
}

function InvoicesTab({
  vendors,
  invoices,
  qbCosts,
  canAct,
  onCreate,
  onDelete,
  onImport,
}: {
  period: string;
  vendors: IntegrationVendor[];
  invoices: IntegrationInvoice[];
  qbCosts: IntegrationQbCost[];
  canAct: boolean;
  onCreate: ReturnType<typeof useCreateIntegrationInvoice>;
  onDelete: ReturnType<typeof useDeleteIntegrationInvoice>;
  onImport: ReturnType<typeof useImportIntegrationLines>;
}) {
  const [adding, setAdding] = useState(false);
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);

  /** One bulk row = create the invoice, then import its lines if the file is a
   *  parseable line export. A PDF still records the header and attaches. */
  async function saveBulkRow(row: BulkRow) {
    const created = await onCreate.mutateAsync({
      vendor_id: row.vendorId,
      invoice_number: row.invoiceNumber,
      invoice_date: row.invoiceDate || null,
      service_period_start: row.serviceStart || null,
      service_period_end: row.serviceEnd || null,
      subtotal: Number(row.subtotal || 0),
      tax: Number(row.tax || 0),
      total: Number(row.total || 0),
      invoiced_quantity: row.quantity ? Number(row.quantity) : null,
    });
    if (/\.(xlsx|xlsm|csv|tsv|txt)$/i.test(row.file.name)) {
      await onImport.mutateAsync({ invoiceId: created.id, file: row.file });
    }
  }
  const [form, setForm] = useState({
    vendor_id: "",
    invoice_number: "",
    invoice_date: "",
    service_period_start: "",
    service_period_end: "",
    subtotal: "",
    tax: "",
    total: "",
    invoiced_quantity: "",
  });

  async function submit() {
    try {
      await onCreate.mutateAsync({
        vendor_id: form.vendor_id,
        invoice_number: form.invoice_number,
        invoice_date: form.invoice_date || null,
        service_period_start: form.service_period_start || null,
        service_period_end: form.service_period_end || null,
        subtotal: Number(form.subtotal || 0),
        tax: Number(form.tax || 0),
        total: Number(form.total || 0),
        invoiced_quantity: form.invoiced_quantity ? Number(form.invoiced_quantity) : null,
      });
      toast.success("Invoice recorded");
      setAdding(false);
      setForm({ ...form, invoice_number: "", subtotal: "", tax: "", total: "", invoiced_quantity: "" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't record that invoice");
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>, invoiceId: string) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const res = await onImport.mutateAsync({ invoiceId, file });
      toast.success(`Imported ${res.line_count} lines`);
    } catch (err) {
      // The arithmetic gate rejects a parse that doesn't tie to the header —
      // surfaced verbatim, because the number is the whole point.
      toast.error(err instanceof Error ? err.message : "Import failed");
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">Vendor invoices</CardTitle>
        {canAct && (
          <div className="flex items-center gap-2">
            {/* Bulk first — a month is 8-10 invoices, so one-at-a-time is the
                exception, not the default. */}
            <label className="inline-flex h-8 cursor-pointer items-center rounded-md border px-3 text-sm font-medium hover:bg-muted">
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              Upload invoices
              <input
                type="file"
                multiple
                accept=".xlsx,.xlsm,.csv,.tsv,.txt,.pdf"
                className="hidden"
                onChange={(e) => {
                  const picked = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  if (picked.length) setBulkFiles(picked);
                }}
              />
            </label>
            <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Record one
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {adding && (
          <div className="grid gap-2 rounded border p-3 md:grid-cols-4">
            <label className="text-xs" htmlFor="ie-vendor">
              Vendor
              <select
                className="mt-1 h-9 w-full rounded border bg-background px-2 text-sm"
                id="ie-vendor"
                value={form.vendor_id}
                onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
              >
                <option value="">Select…</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.mode})
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs" htmlFor="ie-invoice-number">
              Invoice #
              <Input
                className="mt-1"
                id="ie-invoice-number"
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              />
            </label>
            <label className="text-xs" htmlFor="ie-invoice-date">
              Invoice date
              <Input
                type="date"
                className="mt-1"
                id="ie-invoice-date"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
              />
            </label>
            <label className="text-xs" htmlFor="ie-qty">
              Quantity (count mode)
              <Input
                className="mt-1"
                id="ie-qty"
                value={form.invoiced_quantity}
                onChange={(e) => setForm({ ...form, invoiced_quantity: e.target.value })}
              />
            </label>
            <label className="text-xs" htmlFor="ie-sp-start">
              Service period start
              <Input
                type="date"
                className="mt-1"
                id="ie-sp-start"
                value={form.service_period_start}
                onChange={(e) => setForm({ ...form, service_period_start: e.target.value })}
              />
            </label>
            <label className="text-xs" htmlFor="ie-sp-end">
              Service period end
              <Input
                type="date"
                className="mt-1"
                id="ie-sp-end"
                value={form.service_period_end}
                onChange={(e) => setForm({ ...form, service_period_end: e.target.value })}
              />
            </label>
            <label className="text-xs" htmlFor="ie-subtotal">
              Subtotal
              <Input
                className="mt-1"
                id="ie-subtotal"
                value={form.subtotal}
                onChange={(e) => setForm({ ...form, subtotal: e.target.value })}
              />
            </label>
            <label className="text-xs" htmlFor="ie-total">
              Total
              <Input
                className="mt-1"
                id="ie-total"
                value={form.total}
                onChange={(e) => setForm({ ...form, total: e.target.value })}
              />
            </label>
            <p className="col-span-full text-xs text-muted-foreground">
              The <strong>service period</strong> is the month the invoice covers — never
              assume it from the invoice date. ISI bills July on a 7/31 invoice, PM
              Attendant bills August in advance on 8/1, and the Driverse vendors bill July
              on an 8/12 invoice.
            </p>
            <div className="col-span-full flex gap-2">
              <Button size="sm" onClick={submit} disabled={!form.vendor_id || !form.invoice_number}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {invoices.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No invoices recorded for this month yet.
          </p>
        ) : (
          <DataTable
            rows={invoices}
            rowKey={(r) => r.id}
            columns={[
              {
                key: "vendor_name",
                header: "Vendor",
                render: (r) => (
                  <span className="inline-flex items-center gap-2">
                    {r.vendor_name}{" "}
                    <HintedStatus value={r.vendor_mode} hint={MODE_HINT[r.vendor_mode]} />
                  </span>
                ),
              },
              { key: "invoice_number", header: "Invoice #" },
              {
                key: "service_period_start",
                header: "Service period",
                render: (r) =>
                  r.service_period_start
                    ? `${r.service_period_start} → ${r.service_period_end ?? "?"}`
                    : "—",
              },
              {
                key: "total",
                header: "Total",
                align: "right",
                render: (r) => money(r.total),
              },
              {
                key: "line_count",
                header: "Lines",
                align: "right",
                render: (r) =>
                  r.vendor_mode === "count" ? (
                    <span className="text-xs text-muted-foreground">
                      qty {r.invoiced_quantity ?? "—"}
                    </span>
                  ) : r.vendor_mode === "listed" ? (
                    <span className="text-xs text-muted-foreground">n/a</span>
                  ) : (
                    r.line_count
                  ),
              },
              {
                key: "parse_status",
                header: "Parse",
                render: (r) =>
                  r.parse_status === "rejected" ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs text-destructive"
                      title={r.parse_note ?? ""}
                    >
                      <AlertTriangle className="h-3.5 w-3.5" /> rejected
                    </span>
                  ) : r.parse_status === "ok" ? (
                    <span className="text-xs text-success">ok</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  ),
              },
              {
                key: "actions",
                header: "",
                align: "right",
                className: "w-28",
                render: (r) =>
                  canAct ? (
                    <span className="inline-flex gap-1">
                      {r.vendor_mode === "line" && (
                        // Each row owns its own input, so the upload target is
                        // structural rather than a ref set by a prior click —
                        // which also makes it directly driveable in a test.
                        <label
                          title="Import line detail (.xlsx / .csv)"
                          aria-label={`Import lines for ${r.invoice_number}`}
                          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted"
                        >
                          <Upload className="h-3.5 w-3.5" />
                          <input
                            type="file"
                            accept=".xlsx,.xlsm,.csv,.tsv,.txt"
                            className="hidden"
                            onChange={(e) => handleFile(e, r.id)}
                          />
                        </label>
                      )}
                      <IconButton
                        title={`Delete ${r.invoice_number}`}
                        hoverClassName="hover:bg-danger-light hover:text-danger"
                        onClick={() => {
                          void onDelete.mutateAsync(r.id).then(
                            () => toast.success("Invoice removed"),
                            (err: unknown) =>
                              toast.error(
                                err instanceof Error ? err.message : "Couldn't remove it",
                              ),
                          );
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </span>
                  ) : null,
              },
            ]}
          />
        )}

        <QbCostsPanel qbCosts={qbCosts} />
      </CardContent>

      <IntegrationBulkUploadDialog
        open={bulkFiles.length > 0}
        onOpenChange={(v) => !v && setBulkFiles([])}
        vendors={vendors}
        files={bulkFiles}
        onSubmit={saveBulkRow}
      />
    </Card>
  );
}

/** What QuickBooks actually paid, beside what this close recorded.
 *
 *  The point is coverage, not a tie: QuickBooks posts on transaction date while
 *  invoices here are keyed to the service period they cover, so deltas are
 *  normal. The row worth acting on is one we PAID and never recorded. */
function QbCostsPanel({ qbCosts }: { qbCosts: IntegrationQbCost[] }) {
  if (qbCosts.length === 0) return null;
  const paid = qbCosts.reduce((sum, c) => sum + c.amount, 0);
  const unrecorded = qbCosts.filter((c) => c.unrecorded);

  return (
    <div className="mt-6 border-t pt-4">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold">
          QuickBooks — Integrations (5182){" "}
          <span className="font-normal text-muted-foreground">
            · {money(paid)} paid this month
          </span>
        </h3>
        {unrecorded.length > 0 && (
          <span className="text-xs font-medium text-warning">
            {unrecorded.length} paid with no invoice recorded
          </span>
        )}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Posted spend from bills and card purchases. QuickBooks posts on{" "}
        <strong>transaction date</strong> and invoices here are keyed to the{" "}
        <strong>service period</strong> they cover, so a delta is expected — this is
        here to catch a vendor billing us that never reached the close at all.
        MOTOR (prepaid) and AutoNetTV (partner-services revenue) post elsewhere and
        will not appear.
      </p>
      <DataTable
        rows={qbCosts}
        rowKey={(r) => r.qb_vendor_name}
        columns={[
          { key: "qb_vendor_name", header: "QuickBooks vendor" },
          {
            key: "matched_vendors",
            header: "Covers",
            render: (r) =>
              r.matched_vendors.length ? (
                <span className="text-xs">{r.matched_vendors.join(", ")}</span>
              ) : (
                <span className="text-xs text-warning">not in the vendor catalog</span>
              ),
          },
          {
            key: "amount",
            header: "Paid",
            align: "right",
            render: (r) => money(r.amount),
          },
          {
            key: "recorded_total",
            header: "Recorded",
            align: "right",
            render: (r) =>
              r.unrecorded ? (
                <span className="font-medium text-warning">none</span>
              ) : (
                money(r.recorded_total)
              ),
          },
          {
            key: "delta",
            header: "Delta",
            align: "right",
            render: (r) => (
              <span className={cn(r.unrecorded && "font-medium text-warning")}>
                {money(r.delta)}
              </span>
            ),
          },
          {
            key: "sources",
            header: "Source",
            render: (r) => <span className="text-xs text-muted-foreground">{r.sources}</span>,
          },
        ]}
      />
    </div>
  );
}

/** Live on the feed, absent from every invoice — the reverse of the worklist.
 *
 *  Collapsed by default and kept out of the reconciled-lines table on purpose:
 *  ISI alone contributes 312 rows, which would bury the 187 lines someone is
 *  actually here to review. Different question, different place. */
function NotInvoicedPanel({ groups }: { groups: IntegrationNotInvoiced[] }) {
  if (groups.length === 0) return null;
  const total = groups.reduce((sum, g) => sum + g.total, 0);

  return (
    <CollapsibleCard
      className="mt-4"
      title="In Cinch, not on any invoice"
      meta={`${total.toLocaleString()} location${total === 1 ? "" : "s"} sent data but aren't billed`}
    >
      <p className="text-xs text-muted-foreground">
        Locations on these vendors&rsquo; feeds that produced transactions in the
        service period and appear on no invoice line. Either a true-up is coming, the
        rooftop is billed under another arrangement, or its feed is mislabelled — all
        three are worth knowing before the vendor tells us. Busiest first.
      </p>
      {groups.map((g) => (
        <div key={g.vendor_slug} className="mt-3">
          <p className="mb-1.5 text-xs font-medium">
            {g.vendor_name}{" "}
            <span className="font-normal text-muted-foreground">
              · {g.total.toLocaleString()} not billed
              {g.shown < g.total && ` · showing the busiest ${g.shown}`}
            </span>
          </p>
          {/* By company first. A single brand dominating the list usually means
              a stale entity_source rather than 138 separate billing errors —
              Grease Monkey's rooftops moved to the Full Speed warehouse and
              kept their old ISI label. Seeing that shape takes one glance here
              and a long scroll otherwise. */}
          <CompanyRollup locations={g.locations} />
          <DataTable
            rows={g.locations}
            rowKey={(r) => `${g.vendor_slug}:${r.vendor_ref ?? r.location_id}`}
            columns={[
              {
                key: "vendor_ref",
                header: "Vendor ref",
                render: (r) => (
                  <span className="font-mono text-xs">{r.vendor_ref ?? "—"}</span>
                ),
              },
              {
                key: "location_name",
                header: "Cinch location",
                render: (r) => (
                  <span>
                    {r.location_name ?? "—"}
                    {r.company_name && (
                      <span className="block text-xs text-muted-foreground">
                        {r.company_name}
                        {r.city ? ` · ${r.city}` : ""}
                        {r.state ? `, ${r.state}` : ""}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                key: "txns_in_period",
                header: "Txns",
                align: "right",
                sortable: true,
                render: (r) => r.txns_in_period.toLocaleString(),
              },
            ]}
          />
        </div>
      ))}
    </CollapsibleCard>
  );
}

/** Which companies the unbilled rooftops belong to, biggest first. */
function CompanyRollup({
  locations,
}: {
  locations: { company_name: string | null; txns_in_period: number }[];
}) {
  const rows = useMemo(() => {
    const by = new Map<string, { locs: number; txns: number }>();
    for (const l of locations) {
      const key = l.company_name ?? "—";
      const prev = by.get(key) ?? { locs: 0, txns: 0 };
      by.set(key, { locs: prev.locs + 1, txns: prev.txns + l.txns_in_period });
    }
    return [...by.entries()].sort((a, b) => b[1].locs - a[1].locs);
  }, [locations]);

  if (rows.length <= 1) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {rows.slice(0, 8).map(([name, v]) => (
        <span
          key={name}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs"
          title={`${v.txns.toLocaleString()} transactions in the period`}
        >
          <span className="max-w-[16rem] truncate">{name}</span>
          <span className="font-medium tabular-nums">{v.locs}</span>
        </span>
      ))}
      {rows.length > 8 && (
        <span className="px-1 py-0.5 text-xs text-muted-foreground">
          +{rows.length - 8} more
        </span>
      )}
    </div>
  );
}

function HowToTab() {
  return (
    <Card>
      <CardContent className="space-y-6 py-5 text-sm leading-relaxed">
        <section>
          <h3 className="mb-1 font-semibold">What this step is for</h3>
          <p className="text-muted-foreground">
            Vendors bill us per-rooftop connection fees to pull data out of their DMS.
            Nothing upstream ties an invoice line to the location it is for, so when a
            customer churns or a feed quietly dies, the fee keeps billing. This step
            reconciles what the vendor <strong>billed</strong>, whether the integration
            is still <strong>connected</strong>, and whether data actually{" "}
            <strong>arrived</strong>.
          </p>
        </section>

        <section>
          <h3 className="mb-1 font-semibold">The order to work it</h3>
          <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
            <li>
              <strong>Invoices</strong> — record every vendor invoice for the month.
            </li>
            <li>
              <strong>Run match</strong> — freezes a verdict per line against Cinch.
            </li>
            <li>
              <strong>Mapping</strong> — resolve anything that came back unmapped, then
              re-run the match so the fix applies.
            </li>
            <li>
              <strong>Reconciliation</strong> — work the exceptions, approve or flag.
            </li>
            <li>Mark the step complete. An open flag blocks that.</li>
          </ol>
        </section>

        <section>
          <h3 className="mb-1 font-semibold">Invoices tab</h3>
          <p className="text-muted-foreground">
            <strong>Upload invoices</strong> takes the whole month at once and opens a
            mapping dialog — vendor and invoice number are guessed from the filename;
            check them. <strong>Record one</strong> is the single-invoice form.
          </p>
          <p className="mt-2 text-muted-foreground">
            The <strong>service period</strong> is the field that matters most, and it
            is <em>not</em> the invoice date. ISI bills July on a 7/31 invoice, PM
            Attendant bills August in advance on 8/1, and the Driverse vendors bill
            July on an 8/12 invoice. Get this wrong and the line reconciles against the
            wrong month&rsquo;s data.
          </p>
          <p className="mt-2 text-muted-foreground">
            For line-mode vendors, the upload icon on a row imports the per-location
            detail (.xlsx / .csv). The import is <strong>rejected unless the lines sum
            to the invoice header</strong>, so a mis-parsed file can never quietly
            become the reconciliation&rsquo;s truth.
          </p>
          <p className="mt-2 text-muted-foreground">
            The <strong>QuickBooks — Integrations (5182)</strong> panel shows what we
            actually paid. It is there to catch a vendor billing us that never reached
            this close; deltas against recorded invoices are normal, because QuickBooks
            posts on transaction date.
          </p>
        </section>

        <section>
          <h3 className="mb-1 font-semibold">Three kinds of vendor</h3>
          <ul className="space-y-1.5 text-muted-foreground">
            <li>
              <StatusBadge value="line" /> One invoice line per location (ISI, PM
              Attendant, RO Writer). Every line is matched and gets a verdict.
            </li>
            <li>
              <StatusBadge value="count" /> The invoice states a quantity and names
              nobody (Vehlo, Sage, Autodata, Egenuity). The finding is the delta against
              the rooftops we can evidence. Where a company&rsquo;s rooftop count equals
              the billed quantity, that is a strong lead about who the invoice covers.
            </li>
            <li>
              <StatusBadge value="listed" /> Recorded so the month&rsquo;s total is
              complete but never matched (MOTOR is a flat licence, AutoNetTV a product
              we resell). These show usage only.
            </li>
          </ul>
        </section>

        <section>
          <h3 className="mb-1 font-semibold">Reconciliation tab — the verdicts</h3>
          <ul className="space-y-1.5 text-muted-foreground">
            {[
              "ok",
              "no_data",
              "churned",
              "cancelling",
              "not_billing",
              "unmapped",
              "duplicate",
              "count_delta",
              "cancellation_not_honoured",
            ].map((v) => (
              <li key={v} className="flex items-baseline gap-2">
                <span className="shrink-0">
                  <StatusBadge value={v} />
                </span>
                <span>{VERDICT_META[v]?.hint}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-muted-foreground">
            Approve a line you are happy to keep paying. Flag one you want raised with
            the vendor — <strong>an open flag blocks completing the step</strong>.
          </p>
        </section>

        <section>
          <h3 className="mb-1 font-semibold">Mapping tab</h3>
          <p className="text-muted-foreground">
            Vendor location ids are matched to Cinch locations automatically where they
            agree. Anything left over lands here. Search, then confirm — confirming is
            what makes a mapping real, and it is the only thing that clears an{" "}
            <em>unmapped</em> verdict. A confident-but-wrong match would mark a line
            reconciled against the wrong rooftop and hide a genuine leak, so the
            candidate list only ever proposes.
          </p>
          <p className="mt-2 text-muted-foreground">
            Mappings are <strong>saved permanently</strong>. Month two, most of this is
            already done. Re-run the match after confirming so the new mapping applies.
          </p>
        </section>

        <section>
          <h3 className="mb-1 font-semibold">Things that will trip you up</h3>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>
              A vendor cancellation lags a billing cycle, so a line you actioned comes
              back once more. Record the disposition rather than re-investigating it.
            </li>
            <li>
              A location&rsquo;s evidence is the transactions it sent — never a
              company&rsquo;s active flag, which is false on plenty of live,
              heavily-transacting customers.
            </li>
            <li>
              Re-running the match keeps your verdicts only where the line&rsquo;s facts
              are unchanged. Notes always survive.
            </li>
          </ul>
        </section>
      </CardContent>
    </Card>
  );
}

function MappingTab({
  period,
  unmapped,
  vendors,
  canAct,
}: {
  period: string;
  unmapped: IntegrationLine[];
  vendors: IntegrationVendor[];
  canAct: boolean;
}) {
  const [selected, setSelected] = useState<IntegrationLine | null>(null);
  const [query, setQuery] = useState("");
  const confirm = useConfirmIntegrationMapping(period);

  const vendorId = useMemo(
    () => vendors.find((v) => v.slug === selected?.vendor_slug)?.id ?? null,
    [vendors, selected],
  );
  const { data: candidates, isLoading } = useIntegrationCandidates(period, vendorId, query);

  async function confirmMapping(candidate: { location_id: string | null; location_name: string | null; company_id?: string | null; company_name: string | null }) {
    if (!selected || !vendorId) return;
    try {
      await confirm.mutateAsync({
        vendor_id: vendorId,
        vendor_location_ref: selected.vendor_location_ref ?? selected.item_key,
        vendor_location_name: selected.vendor_location_name,
        location_id: candidate.location_id,
        location_name: candidate.location_name,
        company_id: candidate.company_id ?? null,
        company_name: candidate.company_name,
      });
      toast.success("Mapping confirmed — re-run the match to apply it");
      setSelected(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't confirm that mapping");
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Unmapped lines ({unmapped.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {unmapped.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Everything on this month's invoices is mapped.
            </p>
          ) : (
            <DataTable
              rows={unmapped}
              rowKey={(r) => r.item_key}
              onRowClick={(r) => {
                setSelected(r);
                setQuery(r.vendor_location_name ?? r.vendor_location_ref ?? "");
              }}
              columns={[
                { key: "vendor_name", header: "Vendor", render: (r) => r.vendor_name ?? "—" },
                {
                  key: "vendor_location_ref",
                  header: "Ref",
                  render: (r) => (
                    <span className="font-mono text-xs">{r.vendor_location_ref ?? "—"}</span>
                  ),
                },
                {
                  key: "vendor_location_name",
                  header: "Billed as",
                  render: (r) => r.vendor_location_name ?? "—",
                },
                {
                  key: "amount",
                  header: "Amount",
                  align: "right",
                  render: (r) => money(r.amount),
                },
              ]}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {selected
              ? `Map "${selected.vendor_location_name ?? selected.vendor_location_ref}"`
              : "Pick a line to map"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!selected ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Select an unmapped line to search Cinch locations for it.
            </p>
          ) : (
            <>
              <FilterSearch
                value={query}
                onChange={setQuery}
                placeholder="Search Cinch locations…"
              />
              <p className="text-xs text-muted-foreground">
                Ranking only proposes. Confirming is what makes a mapping real — a
                confident-but-wrong match would mark this line reconciled against the wrong
                rooftop and hide a genuine leak.
              </p>
              {isLoading ? (
                <div className="h-32 animate-pulse rounded bg-muted" />
              ) : (
                <ul className="max-h-80 space-y-1 overflow-y-auto">
                  {(candidates ?? []).slice(0, 25).map((c) => (
                    <li
                      key={c.location_id ?? Math.random()}
                      className="flex items-center justify-between gap-2 rounded border p-2 text-sm"
                    >
                      <span>
                        {c.location_name ?? "—"}
                        <span className="block text-xs text-muted-foreground">
                          {[c.company_name, c.city, c.state].filter(Boolean).join(" · ")} ·{" "}
                          {c.txns_in_period.toLocaleString()} txns
                        </span>
                      </span>
                      {canAct && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => confirmMapping(c)}
                          disabled={confirm.isPending}
                        >
                          <Link2 className="mr-1.5 h-3.5 w-3.5" /> Confirm
                        </Button>
                      )}
                    </li>
                  ))}
                  {(candidates ?? []).length === 0 && (
                    <li className="py-6 text-center text-sm text-muted-foreground">
                      No candidates — this vendor may have no{" "}
                      <code className="text-xs">entity_source</code> configured.
                    </li>
                  )}
                </ul>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
